import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css } from "@emotion/react";

export const root =
  ({ isAddedToCart, isOpen }: { isAddedToCart: boolean; isOpen: boolean }) =>
  (theme: ExactTheme) => css`
    cursor: pointer;
    height: 130px;
    position: relative;
    border-radius: 4px;
    flex-shrink: 0;
    min-height: 0;
    border-width: ${isOpen ? "2px" : "2px"};
    border-style: solid;
    border-color: ${isOpen
      ? theme.colorsV2.blue[300]
      : isAddedToCart
      ? theme.colorsV2.blue[50]
      : "transparent"};

    background-color: ${isAddedToCart
      ? "#f8fbff" // designer used color outside the theme for some reason
      : theme.colorsV2.white};
    display: flex;
    column-gap: 15px;
    padding: 8px; // + 2px transparent border = 10px
    :hover {
      box-shadow: ${theme.shadowV2.secondary};
    }
  `;

export const inner =
  ({ isAddedToCart }: { isAddedToCart: boolean }) =>
  (theme: ExactTheme) => css`
    border-radius: 4px;
    height: 100%;
    background-color: ${isAddedToCart
      ? "#f8fbff" // designer used color outside the theme for some reason
      : theme.colorsV2.white};
    display: flex;
    column-gap: 15px;
    padding: 10px;
    :hover {
      box-shadow: ${theme.shadowV2.secondary};
    }
  `;
export const image = () => css`
  width: 117px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;
export const description = () => css`
  flex-grow: 1;
  min-width: 0;
`;
export const title = (theme: ExactTheme) => css`
  color: ${theme.colorsV2.grey[900]};
`;
export const supplierTitle = (theme: ExactTheme) => css`
  color: ${theme.colorsV2.grey[800]};
`;
export const badges =
  ({ isPreviewOpen }: { isPreviewOpen: boolean }) =>
  (theme: ExactTheme) => css`
    width: 92px;
    flex-shrink: 0;
    display: ${isPreviewOpen ? "none" : "flex"};
    flex-direction: column;
    align-items: end;
    gap: 2px;
    @media ${theme.device.desktop} {
      display: flex;
    }
  `;

export const specs =
  ({
    isPreviewOpen,
    isFilterPanelOpen,
    isFullscreen,
  }: {
    isPreviewOpen: boolean;
    isFilterPanelOpen: boolean;
    isFullscreen: boolean;
  }) =>
  (theme: ExactTheme) => {
    return [
      css`
        width: 200px;
        flex-shrink: 0;
        flex-direction: column;
        justify-content: space-between;
      `,
      isFullscreen
        ? css`
            @media ${theme.deviceV2.lessThan.tablet} {
              display: none;
            }

            @media ${theme.deviceV2.greaterThan.tablet} {
              display: ${isPreviewOpen || isFilterPanelOpen ? "none" : "flex"};
            }

            @media ${theme.deviceV2.greaterThan.laptopL} {
              display: ${isPreviewOpen ? "none" : "flex"};
            }
            @media ${theme.deviceV2.greaterThan.desktop} {
              display: flex;
            }
          `
        : css`
            @media ${theme.deviceV2.lessThan.laptop} {
              display: none;
            }
            @media ${theme.deviceV2.greaterThan.laptop} {
              display: ${isPreviewOpen || isFilterPanelOpen ? "none" : "flex"};
            }

            @media ${theme.deviceV2.greaterThan.laptopL} {
              display: ${isPreviewOpen && !isFullscreen ? "none" : "flex"};
            }
            @media ${theme.deviceV2.greaterThan.desktop} {
              display: flex;
            }
          `,
    ];
  };

export const actions = () => css`
  width: 150px;
  flex-shrink: 0;
  position: relative;
`;
export const productNumber = () => css`
  flex-shrink: 1;
`;
export const productImage = ({
  src,
  isPlaceholder,
}: {
  src: string;
  isPlaceholder: boolean;
}) => css`
  background-image: url("${src}");
  background-position: center;
  background-size: ${isPlaceholder ? "50px 50px" : "contain"};
  background-repeat: no-repeat;
  flex-grow: 1;
`;

export const likeButton =
  ({ liked }: { liked: boolean }) =>
  (theme: ExactTheme) => css`
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 25px;
    height: 25px;
    border-radius: 4px;
    background: transparent;
    color: ${liked ? theme.colorsV2.blue[500] : theme.colorsV2.fg.default};
    border: none;
  `;

export const likeContainer = css`
  position: absolute;
  top: -11px;
  right: -11px;
`;
export const addToCartButton = () => css`
  display: flex;
  justify-content: end;
  position: absolute;
  width: 100%;
  top: 50%;
  transform-origin: center;
  transform: translateY(-50%);
`;
export const counter = () => css`
  display: flex;
  justify-content: end;
  position: absolute;
  width: 100%;
  top: 50%;
  transform-origin: center;
  transform: translateY(-50%);
`;

export const specItem = (theme: ExactTheme) => css`
  padding-top: 2px;
  padding-bottom: 1px;
  :first-child {
    padding-top: 0;
  }

  line-height: 21px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px dashed ${theme.colorsV2.grey[100]};
`;

export const specsContainer = css`
  margin: 0;
`;

export const price2 = css({
  position: "absolute",
  bottom: 0,
  right: 0,
  gap: "5px",
  alignItems: "baseline",
  // display: "flex",
  // flexDirection: "column",
  padding: "0 10px",
});

export const totalWrapper = (theme: ExactTheme) => css`
  position: absolute;
  top: 79px;
  width: 100%;
  display: flex;
  align-items: end;
  flex-direction: column;
`;

export const total = (theme: ExactTheme) => css`
  padding-top: 4px;
  border-top: 1px solid ${theme.colorsV2.grey[100]};
  width: 100%;
  text-align: right;
`;

export const totalExplanation = (theme: ExactTheme) => css`
  color: ${theme.colorsV2.grey[600]};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 130px;
`;
